/**
 * Origen de la carga del estudio (chip AppBar / stats).
 * `local` = IndexedDB / archivos de este equipo
 * `r2` = manifest ZIP_RANGE / object storage
 * `legacy-wado` = WADO-URI / DICOMweb (urls https por instancia)
 */

import { MANIFEST_TYPE, detectManifestType } from './manifestNormalize'
import { isDeveloperMode, hasUdvLabAccess } from '../plan/resolveAccess'

export const STUDY_LOAD_SOURCE = {
  R2: 'r2',
  LOCAL: 'local',
  R2_DONE: 'r2-done',
  LOCAL_DONE: 'local-done',
  LEGACY_WADO: 'legacy-wado',
  WADO_DONE: 'wado-done',
}

/**
 * @param {string|null|undefined} source
 * @returns {boolean}
 */
export function isStudyLoadActive(source) {
  return (
    source === STUDY_LOAD_SOURCE.R2 ||
    source === STUDY_LOAD_SOURCE.LOCAL ||
    source === STUDY_LOAD_SOURCE.LEGACY_WADO
  )
}

/**
 * @param {string|null|undefined} source
 * @returns {boolean}
 */
export function isStudyLoadLocal(source) {
  return (
    source === STUDY_LOAD_SOURCE.LOCAL || source === STUDY_LOAD_SOURCE.LOCAL_DONE
  )
}

/**
 * @param {string|null|undefined} source
 * @returns {boolean}
 */
export function isStudyLoadWado(source) {
  return (
    source === STUDY_LOAD_SOURCE.LEGACY_WADO ||
    source === STUDY_LOAD_SOURCE.WADO_DONE
  )
}

/**
 * Etiqueta corta del chip AppBar.
 * @param {string|null|undefined} source
 * @returns {string}
 */
export function studyLoadSourceChipLabel(source) {
  if (isStudyLoadLocal(source) || source === STUDY_LOAD_SOURCE.LOCAL) {
    return 'Local'
  }
  if (isStudyLoadWado(source)) return 'WADO'
  if (
    source === STUDY_LOAD_SOURCE.R2 ||
    source === STUDY_LOAD_SOURCE.R2_DONE
  ) {
    return 'R2'
  }
  return 'R2'
}

/**
 * IP/host de origen de la descarga: solo developer o staff lab (no clínica).
 * @returns {boolean}
 */
export function showStudyLoadOriginHint() {
  return isDeveloperMode() || hasUdvLabAccess()
}

/**
 * Texto de origen para chip/tooltip (developer): host real + IP LAN si se conoce.
 * @param {object|null|undefined} progress
 * @returns {string}
 */
export function formatStudyLoadOriginLabel(progress) {
  if (!progress) return ''
  const origin = progress.originHost ? String(progress.originHost) : ''
  const lan = progress.lanHost ? String(progress.lanHost) : ''
  if (lan && origin && lan !== origin) return `${origin} · LAN ${lan}`
  return lan || origin
}

/**
 * Título del tooltip / panel de stats.
 * @param {string|null|undefined} source
 * @returns {string}
 */
export function studyLoadSourceDownloadTitle(source) {
  if (isStudyLoadLocal(source)) return 'Carga local'
  if (isStudyLoadWado(source)) return 'Descarga (DICOMweb / WADO-URI)'
  return 'Descarga (manifest / R2)'
}

/**
 * @param {object|null|undefined} data
 * @returns {object|null}
 */
function firstManifestInstance(data) {
  if (!data || typeof data !== 'object') return null
  if (Array.isArray(data.series)) {
    for (let i = 0; i < data.series.length; i++) {
      const instances = data.series[i] && data.series[i].instances
      if (!Array.isArray(instances)) continue
      for (let j = 0; j < instances.length; j++) {
        if (instances[j]) return instances[j]
      }
    }
  }
  if (Array.isArray(data.instances) && data.instances[0]) {
    return data.instances[0]
  }
  return null
}

/**
 * Origen de red según tipo de manifest (antes de cache hits → local).
 * @param {object|null|undefined} manifest — raw o canonical
 * @returns {'r2'|'legacy-wado'}
 */
export function resolveStudyLoadSourceFromManifest(manifest) {
  if (!manifest || typeof manifest !== 'object') {
    return STUDY_LOAD_SOURCE.R2
  }
  const type =
    manifest.manifestType === MANIFEST_TYPE.ZIP_RANGE ||
    manifest.manifestType === MANIFEST_TYPE.LEGACY_DIRECT
      ? manifest.manifestType
      : detectManifestType(manifest)

  if (type === MANIFEST_TYPE.ZIP_RANGE) {
    return STUDY_LOAD_SOURCE.R2
  }
  if (type === MANIFEST_TYPE.LEGACY_DIRECT) {
    return STUDY_LOAD_SOURCE.LEGACY_WADO
  }

  const inst = firstManifestInstance(manifest)
  const hasHttp =
    !!inst &&
    ((inst.url && /^https?:\/\//i.test(String(inst.url).trim())) ||
      (inst.dicomUrl && /^https?:\/\//i.test(String(inst.dicomUrl).trim())))
  if (hasHttp) return STUDY_LOAD_SOURCE.LEGACY_WADO
  return STUDY_LOAD_SOURCE.R2
}

/**
 * @param {object|null|undefined} stats
 * @param {{ preferredRemote?: string|null }} [opts]
 * @returns {string}
 */
export function resolveStudyLoadSourceFromStats(stats, opts = {}) {
  const preferredRemote =
    opts.preferredRemote === STUDY_LOAD_SOURCE.LEGACY_WADO
      ? STUDY_LOAD_SOURCE.LEGACY_WADO
      : opts.preferredRemote === STUDY_LOAD_SOURCE.R2
        ? STUDY_LOAD_SOURCE.R2
        : null

  if (!stats || typeof stats !== 'object') {
    return preferredRemote || STUDY_LOAD_SOURCE.R2
  }

  if (stats.loadSource === STUDY_LOAD_SOURCE.LEGACY_WADO) {
    return STUDY_LOAD_SOURCE.LEGACY_WADO
  }
  if (stats.loadSource === STUDY_LOAD_SOURCE.LOCAL) {
    return STUDY_LOAD_SOURCE.LOCAL
  }
  if (stats.loadSource === STUDY_LOAD_SOURCE.R2) {
    return STUDY_LOAD_SOURCE.R2
  }

  const offline = stats.offlineCache || {}
  if (offline.studyFullyCachedAtStart) return STUDY_LOAD_SOURCE.LOCAL
  const hits = Number(offline.fromCacheHits) || 0
  const loadedOk = Number(stats.loadedOk) || 0
  const done = Number(stats.completed) || 0
  const denom = Math.max(loadedOk, done)
  if (offline.studyOffline && denom === 0) return STUDY_LOAD_SOURCE.LOCAL
  if (denom > 0 && hits >= denom * 0.8) return STUDY_LOAD_SOURCE.LOCAL

  return preferredRemote || STUDY_LOAD_SOURCE.R2
}

/**
 * Fuente al terminar la carga (el chip activo se oculta; las stats siguen).
 * @param {string|null|undefined} prevSource
 * @param {object|null|undefined} progress
 * @returns {string|null}
 */
export function finishStudyLoadSource(prevSource, progress) {
  const hadProgress = !!progress
  const wasActive = isStudyLoadActive(prevSource)
  if (!hadProgress && !wasActive) return null
  if (
    prevSource === STUDY_LOAD_SOURCE.LEGACY_WADO ||
    prevSource === STUDY_LOAD_SOURCE.WADO_DONE
  ) {
    return STUDY_LOAD_SOURCE.WADO_DONE
  }
  const active = wasActive
    ? prevSource
    : resolveStudyLoadSourceFromStats(progress)
  if (active === STUDY_LOAD_SOURCE.LOCAL) return STUDY_LOAD_SOURCE.LOCAL_DONE
  if (active === STUDY_LOAD_SOURCE.LEGACY_WADO) {
    return STUDY_LOAD_SOURCE.WADO_DONE
  }
  return STUDY_LOAD_SOURCE.R2_DONE
}

/** Error estándar al abortar carga de estudio. */
export function createStudyLoadAbortError(message) {
  const err = new Error(message || 'Study load cancelled')
  err.name = 'AbortError'
  return err
}

/**
 * @param {unknown} e
 * @returns {boolean}
 */
export function isStudyLoadAbortError(e) {
  if (!e) return false
  if (e.name === 'AbortError') return true
  const msg = String(e.message || e)
  return /stud(y|ies) load cancelled|aborted|The operation was aborted/i.test(msg)
}

/**
 * @param {AbortSignal|null|undefined} signal
 * @param {(() => boolean)|null|undefined} isCancelled
 * @returns {boolean}
 */
export function isStudyLoadCancelled(signal, isCancelled) {
  if (signal && signal.aborted) return true
  if (typeof isCancelled === 'function') {
    try {
      return !!isCancelled()
    } catch (e) {
      return false
    }
  }
  return false
}

/**
 * @param {AbortSignal|null|undefined} signal
 * @param {(() => boolean)|null|undefined} isCancelled
 */
export function throwIfStudyLoadCancelled(signal, isCancelled) {
  if (isStudyLoadCancelled(signal, isCancelled)) {
    throw createStudyLoadAbortError()
  }
}
